import { useState, useEffect } from 'react';
import { notificationAPI } from '../../lib/api';
import { Bell, Check, CheckCircle, CreditCard, Calendar, ShieldCheck, Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'payment_reminder':
      return <CreditCard size={20} className="text-amber-500" />;
    case 'appointment_reminder':
      return <Calendar size={20} className="text-blue-500" />;
    case 'consent_expiring':
      return <ShieldCheck size={20} className="text-orange-500" />;
    case 'care_gap_alert':
      return <Warning size={20} className="text-red-500" />;
    case 'payment_received':
      return <CheckCircle size={20} className="text-green-500" />;
    default:
      return <Bell size={20} className="text-teal-500" />;
  }
};

const getNotificationTypeLabel = (type) => {
  switch (type) {
    case 'payment_reminder':
      return 'Payment Reminder';
    case 'appointment_reminder':
      return 'Appointment';
    case 'consent_expiring':
      return 'Consent Expiring';
    case 'care_gap_alert':
      return 'Care Gap';
    case 'payment_received':
      return 'Payment Received';
    case 'welcome':
      return 'Welcome';
    default:
      return 'Notification';
  }
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n)
      );
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, read_at: new Date().toISOString() }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Notifications
          </h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            data-testid="mark-all-read-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
          >
            <Check size={18} />
            Mark all as read
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'unread', 'read'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}-btn`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-teal-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'unread' && unreadCount > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <Bell size={48} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No notifications</h3>
          <p className="text-slate-500">
            {filter === 'unread' ? 'You have no unread notifications' : 'You have no notifications yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              data-testid={`notification-${notification.id}`}
              className={`bg-white rounded-xl shadow-sm border transition-all ${
                notification.read
                  ? 'border-slate-200'
                  : 'border-teal-200 bg-teal-50/30'
              }`}
            >
              <div className="p-4 flex items-start gap-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  notification.read ? 'bg-slate-100' : 'bg-white shadow-sm'
                }`}>
                  {getNotificationIcon(notification.notification_type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      notification.read
                        ? 'bg-slate-100 text-slate-500'
                        : 'bg-teal-100 text-teal-700'
                    }`}>
                      {getNotificationTypeLabel(notification.notification_type)}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatDate(notification.created_at)}
                    </span>
                  </div>
                  <h4 className={`font-medium mb-1 ${notification.read ? 'text-slate-700' : 'text-slate-900'}`}>
                    {notification.title}
                  </h4>
                  <p className={`text-sm ${notification.read ? 'text-slate-500' : 'text-slate-600'}`}>
                    {notification.message}
                  </p>
                  
                  {/* Additional data */}
                  {notification.data && (
                    <div className="mt-2 p-2 bg-slate-50 rounded-lg text-sm text-slate-600">
                      {notification.data.amount && (
                        <span className="font-medium">${notification.data.amount.toFixed(2)} due</span>
                      )}
                      {notification.data.appointment_time && (
                        <span>{new Date(notification.data.appointment_time).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!notification.read && (
                  <button
                    onClick={() => markAsRead(notification.id)}
                    data-testid={`mark-read-${notification.id}`}
                    className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    title="Mark as read"
                  >
                    <Check size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
